import { useMember } from "api/members";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { ProgressComponent } from "components/Table/ProgressComponent";
import { StatusValue } from "components/Table/StatusValue";
import { calculateCount } from "components/Table/tableHelpers";
import { ExternalLinkTooltipContent } from "components/Tooltips/ExternalLinkTooltip";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useNavigate } from "react-router-dom";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { USER_CAMPAIGNS_STATUS_LIST_ID } from "state/tables/types";
import { ListingActions, ListResponse } from "types/listing";
import { MemberCampaignsStatus } from "types/members";
import { links } from "utilities/links";

export function CampaignsStatus({ memberID }: { memberID: string }) {
  const navigate = useNavigate();
  const { message } = useMessage();
  const { listing, updateListing } = useListing(USER_CAMPAIGNS_STATUS_LIST_ID);
  const { setColumnOrdering } = useTablesStateHelpers();

  const { useMembersCampaigns } = useMember(memberID);
  const { data: membersCampaigns } = useMembersCampaigns({
    filters: [
      {
        condition: "",
        query: "showAll",
        value: true,
      },
      {
        condition: "neq",
        query: "trigger",
        value: "leaderboard",
      },
    ],
    order: listing.order || "asc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "name",
  }) as { data: ListResponse };

  const headItems = createHeadItems([
    {
      headerText: message({ id: "campaignName" }),
      isSortable: true,
      property: "name",
    },
    {
      headerText: message({ id: "status" }),
      property: "status",
      isSortable: false,
    },
    {
      align: "right",
      headerText: message({ id: "memberUnitsLimitation" }),
      property: "memberUnitsLimitation",
      tooltip: (
        <ExternalLinkTooltipContent
          name="memberUnitsLimitation"
          text={message({ id: "memberUnitsLimitationInfo" })}
        />
      ),
    },
    {
      align: "right",
      headerText: message({ id: "memberCampaignCompletion" }),
      property: "memberCampaignCompletion",
      tooltip: (
        <ExternalLinkTooltipContent
          name="memberCampaignCompletion"
          text={message({ id: "memberCampaignCompletionInfo" })}
        />
      ),
    },
    {
      align: "right",
      headerText: message({ id: "campaignUnitsLimitation" }),
      property: "campaignUnitsLimitation",
      tooltip: (
        <ExternalLinkTooltipContent
          name="campaignUnitsLimitation"
          text={message({ id: "campaignUnitsLimitationInfo" })}
        />
      ),
    },
  ]);

  function createItems(items: MemberCampaignsStatus[]): Row[] {
    return items.map((item) => {
      const limitUsagesExecutionsPerMember =
        item.limitUsages?.executionsPerMember;
      const limitsExecutionsPerMember = item.limits?.executionsPerMember;
      const limitUsagesPointsPerMember = item.limitUsages?.pointsPerMember;
      const limitsPointsPerMember = item.limits?.pointsPerMember;
      const limitUsagesPoints = item.limitUsages?.points;
      const limitsPoints = item.limits?.points;
      const areLimitUsagesExecutionsPerMemberTypeofNumber =
        typeof limitUsagesExecutionsPerMember?.currentValue === "number" &&
        typeof limitUsagesExecutionsPerMember?.limitValue === "number";
      const areLimitUsagesPointsPerMemberTypeofNumber =
        typeof limitUsagesPointsPerMember?.currentValue === "number" &&
        typeof limitUsagesPointsPerMember.limitValue === "number";
      const areLimitUsagesPointsTypeofNumber =
        typeof limitUsagesPoints?.currentValue === "number" &&
        typeof limitUsagesPoints.limitValue === "number";

      return {
        id: item.campaignId,
        cells: [
          {
            content: item.name,
            property: "name",
          },
          {
            content: <StatusValue status={item.active} />,
            property: "status",
          },
          {
            align: "right",
            content: (
              <ProgressComponent
                isActive={item?.active}
                isLimited={areLimitUsagesPointsPerMemberTypeofNumber}
                limitsType={limitsPointsPerMember}
                limitUsages={limitUsagesPointsPerMember}
              />
            ),
            property: "memberUnitsLimitation",
          },
          {
            align: "right",
            content: (
              <ProgressComponent
                isActive={item?.active}
                isLimited={areLimitUsagesExecutionsPerMemberTypeofNumber}
                limitsType={limitsExecutionsPerMember}
                limitUsages={limitUsagesExecutionsPerMember}
              />
            ),
            property: "memberCampaignCompletion",
          },
          {
            align: "right",
            content: (
              <ProgressComponent
                isActive={item?.active}
                isLimited={areLimitUsagesPointsTypeofNumber}
                limitsType={limitsPoints}
                limitUsages={limitUsagesPoints}
              />
            ),
            property: "campaignUnitsLimitation",
          },
        ],
        metadata: { disabled: !item.active },
      };
    });
  }

  const items = membersCampaigns?.items
    ? createItems(membersCampaigns.items)
    : [];

  return (
    <OLTable
      actions={{
        body: [
          {
            action: (
              id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
            ) =>
              navigate(links.campaign(`${id}`), {
                state: { prevPath: links.singleMemberCampaigns(memberID) },
              }),
            label: message({ id: "seeDetails" }),
          },
        ],
      }}
      columns={membersCampaigns && headItems}
      data-test-id="single-member-campaigns"
      onSort={(prop) =>
        updateListing({
          payload: prop,
          type: ListingActions.SET_SORT,
        })
      }
      onRowClick={(id: string) => {
        navigate(links.campaign(id as string), {
          state: { prevPath: links.singleMemberCampaigns(memberID) },
        });
      }}
      pagination={{
        count: calculateCount(
          membersCampaigns?.total.filtered,
          membersCampaigns?.total.all,
        ),
        onChangePage: (page) =>
          updateListing({ payload: page, type: ListingActions.CHANGE_PAGE }),
        onChangeRowsPerPage: (rows) =>
          updateListing({
            payload: rows,
            type: ListingActions.CHANGE_ROWS_PER_PAGE,
          }),
        page: listing.page,
        rowsPerPage: listing.rowsPerPage,
      }}
      rows={membersCampaigns && items}
      sort={{
        order: listing.order || "asc",
        property: listing.property || "name",
        rowsPerPage: listing.rowsPerPage,
        page: listing.page,
      }}
      setColumnOrdering={setColumnOrdering}
      tableName={USER_CAMPAIGNS_STATUS_LIST_ID}
    />
  );
}
